import { Button } from '@eppendorf/vnls-react-components';
import { useBreakpoint } from '@eppendorf/vnls-react-components-preview';
import { FormEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getTimezoneLabel, TIMEZONE_OPTIONS } from '$shared/utils/timezone.utils';

import { Autocomplete } from '$components/autocomplete';
import { Overview } from '$components/overview-box/overview-box';
import { Tabs } from '$components/tabs/tabs';
import {
  useOrganizationSettings,
  useUpdateOrganizationSettings,
} from '$features/subscription/organization-settings.api';

import './organization-settings.scss';

export function OrganizationSettings() {
  const { t } = useTranslation();
  const isMaxMd = useBreakpoint({ max: 'md' });
  const { data, isError, refetch } = useOrganizationSettings();
  const update = useUpdateOrganizationSettings();
  const [selectedTimezone, setSelectedTimezone] = useState<string | null>(null);
  const timezone = selectedTimezone ?? data?.timezone ?? '';

  const options = useMemo(() => {
    if (
      data?.timezone &&
      !TIMEZONE_OPTIONS.some((option) => option.value === data.timezone)
    ) {
      return [
        ...TIMEZONE_OPTIONS,
        { value: data.timezone, label: getTimezoneLabel(data.timezone) },
      ];
    }
    return TIMEZONE_OPTIONS;
  }, [data?.timezone]);

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (timezone && timezone !== data?.timezone) {
      update.mutate({ timezone }, { onSuccess: () => setSelectedTimezone(null) });
    }
  }

  return (
    <Overview
      className={`box--mobile-fullscreen ${isMaxMd ? 'box--no-shadow' : ''} organization-settings`}
    >
      <Overview.Header>
        <h1 className="title">{t('organizationSettings.title')}</h1>
      </Overview.Header>
      <Overview.Body>
        <Tabs
          routes={[
            { path: 'organization', label: t('organizationSettings.organization') },
          ]}
          isActivePath={() => true}
          handleTabClick={() => undefined}
        />
        <section
          role="tabpanel"
          id="tabpanel-organization"
          aria-labelledby="tab-organization"
          className="m-top-xl"
        >
          {isError && (
            <div role="alert">
              <p className="m-bottom-m">{t('errors.informationCouldNotBeLoaded')}</p>
              <Button variant="tertiary" size="small" onClick={() => refetch()}>
                {t('shared.tryAgain')}
              </Button>
            </div>
          )}
          {data && (
            <form onSubmit={onSubmit} aria-label={t('organizationSettings.organization')}>
              <div className="m-bottom-xl">
                <p
                  className="font-weight-semibold m-bottom-s"
                  id="organization-timezone-label"
                >
                  {t('organizationSettings.timezone')}
                </p>
                <div className="organization-settings__timezone">
                  <Autocomplete
                    value={options.find((option) => option.value === timezone)}
                    placeholder={t('organizationSettings.selectTimezone')}
                    onChange={(option) => {
                      setSelectedTimezone(option.value);
                      update.reset();
                    }}
                    disabled={update.isPending}
                    options={options}
                    noOptionsMessage={() => t('organizationSettings.noTimezoneOptions')}
                    ariaLabelledBy="organization-timezone-label"
                  />
                </div>
                <p className="m-top-s" id="organization-timezone-description">
                  {t('organizationSettings.timezoneDescription')}
                </p>
              </div>
              <Button
                type="submit"
                disabled={!timezone || timezone === data.timezone || update.isPending}
              >
                {t('shared.saveChanges')}
              </Button>
              {update.isError && (
                <p role="alert">{t('organizationSettings.saveError')}</p>
              )}
            </form>
          )}
        </section>
      </Overview.Body>
    </Overview>
  );
}
